import { type ChangeEvent, type FocusEvent, useCallback } from 'react';
import {
  ariaDescribedByIds,
  descriptionId,
  getTemplate,
  labelValue,
  schemaRequiresTrueValue,
  type FormContextType,
  type RJSFSchema,
  type StrictRJSFSchema,
  type WidgetProps,
} from '@rjsf/utils';

export function CustomCheckboxWidget<T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(props: WidgetProps<T, S, F>) {
  const { schema, uiSchema, options, id, value, disabled, readonly, label, hideLabel, autofocus = false, onBlur, onFocus, onChange, registry } = props;

  const DescriptionFieldTemplate = getTemplate<'DescriptionFieldTemplate', T, S, F>('DescriptionFieldTemplate', registry, options);
  // Because an unchecked checkbox will cause html5 validation to fail, only add
  // the "required" attribute if the field value must be "true", due to the
  // "const" or "enum" keywords
  const required = schemaRequiresTrueValue<S>(schema);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked), [onChange]);

  const handleBlur = useCallback((event: FocusEvent<HTMLInputElement>) => onBlur(id, event.target.checked), [onBlur, id]);

  const handleFocus = useCallback((event: FocusEvent<HTMLInputElement>) => onFocus(id, event.target.checked), [onFocus, id]);
  const description = options.description ?? schema.description;

  return (
    <div className={`checkbox ${disabled || readonly ? 'disabled' : ''}`}>
      <label htmlFor={id}>{labelValue(<span>{label}</span>, hideLabel)}</label>

      {!hideLabel && description && (
        <DescriptionFieldTemplate
          id={descriptionId<T>(id)}
          description={description}
          schema={schema}
          uiSchema={uiSchema}
          registry={registry}
        />
      )}

      <input
        type="checkbox"
        id={id}
        name={id}
        checked={typeof value === 'undefined' ? false : value}
        required={required}
        disabled={disabled || readonly}
        autoFocus={autofocus}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        aria-describedby={ariaDescribedByIds<T>(id)}
      />
    </div>
  );
}
